/**
 * styles.ts - Base node styles for automation flow nodes
 *
 * Purpose:
 *   Foundation styles for all node types in the automation flow canvas.
 *   Provides base container styles and type-specific variants.
 *
 * Usage:
 *   import { nodeStyles } from './styles';
 *   <div className={css(nodeStyles.baseContainer, nodeStyles.emailContainer)} />
 *
 * Variants:
 *   - baseContainer: Foundation styles for all nodes
 *   - selected: Visual state for selected nodes
 *   - triggerContainer: Styles specific to trigger nodes
 *   - emailContainer: Styles specific to email step nodes
 *   - taskContainer: Styles specific to task/action item nodes
 *
 * Dependencies:
 *   - None (uses hardcoded colors for simplicity)
 *
 * Related:
 *   - stepNodeStyles.ts (detailed step node styles)
 *   - TriggerNode.tsx
 *   - AutomationStepNodeWrapper.tsx
 */

import { StyleSheet } from 'aphrodite';
import { white, borderOutline, charmGray, brandPrimary, warningLight, warningDark } from '../../../styles/colors';

export const nodeStyles = StyleSheet.create({
	// Base container for all node types
	baseContainer: {
		borderRadius: 12,
		backgroundColor: white,
		paddingTop: 16,
		paddingBottom: 16,
		paddingLeft: 20,
		paddingRight: 20,
		minWidth: 200,
		maxWidth: 300,
		border: `1px solid ${borderOutline}`,
		boxShadow: '0 14px 36px rgba(15, 23, 42, 0.12)',
		transition: 'all 0.2s ease',
		':hover': {
			boxShadow: '0 18px 48px rgba(15, 23, 42, 0.18)',
		},
	},

	// Selected node state
	selected: {
		boxShadow: `0 0 0 2px ${brandPrimary}`,
	},

	// Node header layout
	header: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		marginBottom: 8,
		fontWeight: 600,
		fontSize: 14,
	},

	// Node summary text
	summary: {
		fontSize: 12,
		color: charmGray,
		lineHeight: 1.5,
	},

	// Trigger node styling
	triggerContainer: {
		border: `1px solid ${brandPrimary}`,
		boxShadow: '0 18px 48px rgba(79, 70, 229, 0.14)',
	},

	// Email step node styling
	emailContainer: {
		border: `1px solid ${brandPrimary}`,
	},

	// Task/action item node styling
	taskContainer: {
		border: `1px solid ${warningDark}`,
		backgroundColor: warningLight,
		boxShadow: '0 18px 48px rgba(251, 191, 36, 0.24)',
	},
});
